// Per-room unread MESSAGE counts — the numbers behind the green chips in the
// Groups list, the Groups tab chip, and the sidebar Chat badge. One shared
// hook so every surface shows the SAME number (lifted from RoomsList, which
// previously kept this private and left the other badges counting rooms).
//
// The rooms list holds no messages, so count on demand: a capped fetch of the
// messages newer than my receipt — only for rooms currently unread, re-run
// only when a room's (lastMessageAt, readAt) actually changes (the rooms
// subscription delivers both). Capped at 10 because the UI renders "9+" past
// nine; the exact figure stops mattering.
//
// Parity (FEATURES.chatParity): the same pass also carries the room's MENTION
// count and my MUTE state, off `room_unread_summary_v2()` (migration 0060) —
// one RPC, not three — so the rooms list can dim a muted room's badge and mark
// an `@`. Both degrade: v2 → v1 → per-room counting.
import { useEffect, useMemo, useState } from 'react';
import { db } from './firebase';
import { isRoomUnreadFor, fetchRoomUnreadSummary } from './roomActions';
import { fetchRoomUnreadSummaryV2, type RoomUnreadRow } from './roomActionsParity';
import { USE_SUPABASE, supabase, isoFromMs } from './supabaseClient';
import { FEATURES } from './features';
import { useLiveUnreadSummary } from './live/liveUnread';

export type { RoomUnreadRow };

// ── One RPC for the whole tab ───────────────────────────────────────────────
// Three surfaces mount the summary hook (rooms list, Messages tab chip, sidebar
// badge) and the `rooms` array they key on is republished on every realtime
// patch to any room, so a single incoming message used to fire the RPC three
// times per tab per message — the "4 calls in one second" seen in production
// on 2026-09-17, and the load behind its 5xx rate. Now every consumer shares
// one in-flight request, results are reused for MIN_GAP_MS, and a failed call
// keeps the last good rows instead of blanking the badges.
const SUMMARY_MIN_GAP_MS = 2000;
let summaryInFlight: Promise<RoomUnreadRow[]> | null = null;
let summaryLast: { at: number; userId: string; rows: RoomUnreadRow[] } | null = null;

// Everyone showing the summary in this tab. A fetch that lands AFTER a caller was
// served from the cache must still reach the screen — see `trailing` below.
const summaryListeners = new Set<(rows: RoomUnreadRow[]) => void>();
let trailingTimer: ReturnType<typeof setTimeout> | null = null;

// Rooms this user has just read on THIS device. The badge goes to 0 in the same
// frame, without waiting for the server (the click → write → re-count → repaint
// round trip is 300–800 ms, which reads as lag), and a count fetched while the
// read is still on its way to the database must not bring the badge back.
// `upTo` = timestamp of the newest message that was on screen; a server row is
// only trusted again once the write has settled.
const pendingReads = new Map<string, { upTo: number; settled: boolean }>();

function applyPendingReads(rows: RoomUnreadRow[]): RoomUnreadRow[] {
  if (!pendingReads.size) return rows;
  return rows.map((r) => (pendingReads.has(r.roomId) ? { ...r, unread: 0, mentionCount: 0 } : r));
}
function publish(rows: RoomUnreadRow[]): void {
  const shown = applyPendingReads(rows);
  summaryListeners.forEach((l) => l(shown));
}

async function fetchSummaryNow(userId: string): Promise<RoomUnreadRow[]> {
  if (summaryInFlight) return summaryInFlight;
  summaryInFlight = (async () => {
    try {
      const rows = FEATURES.chatParity
        ? await fetchRoomUnreadSummaryV2()
        : (await fetchRoomUnreadSummary()).map((r) => row(r.roomId, r.unread));
      summaryLast = { at: Date.now(), userId, rows };
      return rows;
    } catch (e) {
      // Transient server error (a 5xx, a timeout): the previous rows are far
      // better than empty badges for the next two seconds.
      if (summaryLast && summaryLast.userId === userId) return summaryLast.rows;
      throw e;
    } finally {
      summaryInFlight = null;
    }
  })();
  return summaryInFlight;
}

async function loadSummaryShared(userId: string): Promise<RoomUnreadRow[]> {
  const now = Date.now();
  if (summaryLast && summaryLast.userId === userId && now - summaryLast.at < SUMMARY_MIN_GAP_MS) {
    // Served from the cache — but whatever made the caller ask (a message, a
    // read receipt) may be NEWER than the cached answer. Without a trailing
    // fetch the badge showed the stale number until something unrelated changed
    // the rooms list: read a room within two seconds of its message arriving and
    // the "1" stayed forever (staging, 2026-09-21). One fetch at the end of the
    // window, shared by every caller, delivered to every listener.
    if (!trailingTimer) {
      const wait = SUMMARY_MIN_GAP_MS - (now - summaryLast.at) + 50;
      trailingTimer = setTimeout(() => {
        trailingTimer = null;
        void fetchSummaryNow(userId).then(publish).catch(() => {});
      }, wait);
    }
    return applyPendingReads(summaryLast.rows);
  }
  return applyPendingReads(await fetchSummaryNow(userId));
}

/**
 * The user has read `roomId` up to `upTo` on this device. Clears that room's
 * badge at once everywhere in this tab, keeps it cleared while `write` (the
 * mark-read request) is in flight, then re-reads the truth from the server.
 */
export function noteRoomRead(userId: string, roomId: string, upTo: number, write?: Promise<unknown>): void {
  const cur = pendingReads.get(roomId);
  if (!cur || cur.upTo < upTo) pendingReads.set(roomId, { upTo, settled: false });
  if (summaryLast) publish(summaryLast.rows);
  const settle = () => {
    const p = pendingReads.get(roomId);
    if (!p || p.upTo > upTo) return;            // a newer read of the same room is still pending
    pendingReads.delete(roomId);
    summaryLast = summaryLast ? { ...summaryLast, at: 0 } : summaryLast; // stale on purpose → a real fetch
    void fetchSummaryNow(userId).then(publish).catch(() => {});
  };
  if (write) void write.then(settle, settle); else settle();
}

/** Test seam: forget the shared cache. */
export function resetRoomUnreadSummaryCache(): void {
  summaryInFlight = null;
  summaryLast = null;
  if (trailingTimer) { clearTimeout(trailingTimer); trailingTimer = null; }
  pendingReads.clear();
  summaryListeners.clear();
}

/** Capped count of messages in a room newer than `afterMs` (Supabase mode).
 *  Same contract as the Firestore query below: at most `cap` rows, room-scoped. */
async function sbCountNewer(roomId: string, afterMs: number, cap: number): Promise<number> {
  const { data, error } = await supabase()
    .from('room_messages')
    .select('id')
    .eq('room_id', roomId)
    .gt('created_at', isoFromMs(afterMs) as string)
    .limit(cap);
  if (error) throw new Error(`room_messages: ${error.message}`);
  return (data || []).length;
}

const row = (roomId: string, unread: number): RoomUnreadRow => ({
  roomId, unread, mentionCount: 0, muted: false,
});

/**
 * Unread + mention + mute for every room, keyed by room id. Rooms with nothing
 * unread are omitted when they are also unmuted and mention-free, so callers
 * can keep treating a missing key as "nothing to show".
 */
export function useRoomUnreadSummary(
  rooms: Parameters<typeof useRoomUnreadSummaryLegacy>[0],
  userId: string | null | undefined
): Record<string, RoomUnreadRow> {
  // Live v2 (FEATURES.liveV2Unread, migration 0076): the rows come from the
  // unread store — read once, kept current by versioned hints — and today's
  // hook is parked (no user → no request). `live` is null whenever today's path
  // must run: flag off (always null, so this is exactly the old hook), migration
  // missing, or the store's first read failed. All logic: services/live/liveUnread.ts.
  const live = useLiveUnreadSummary(rooms, userId);
  const legacy = useRoomUnreadSummaryLegacy(live ? NO_ROOMS : rooms, live ? null : userId);
  return live ?? legacy;
}
const NO_ROOMS: Parameters<typeof useRoomUnreadSummaryLegacy>[0] = [];

function useRoomUnreadSummaryLegacy(
  rooms: Array<{
    id: string;
    lastMessageAt?: number;
    lastMessageSenderId?: string;
    readAt?: Record<string, number>;
  }>,
  userId: string | null | undefined
): Record<string, RoomUnreadRow> {
  const [summary, setSummary] = useState<Record<string, RoomUnreadRow>>({});
  // Rows that arrive outside this hook's own request — the trailing fetch after a
  // cached answer, an optimistic read, the re-read after a read is confirmed.
  useEffect(() => {
    if (!userId || !USE_SUPABASE || !(FEATURES.chatEngineV2 || FEATURES.chatParity)) return;
    const onRows = (rows: RoomUnreadRow[]) => {
      const next: Record<string, RoomUnreadRow> = {};
      for (const r of rows) if (r.unread > 0 || r.mentionCount > 0 || r.muted) next[r.roomId] = r;
      setSummary(next);
    };
    summaryListeners.add(onRows);
    return () => { summaryListeners.delete(onRows); };
  }, [userId]);
  useEffect(() => {
    if (!userId || (!USE_SUPABASE && !db)) return;
    let dead = false;
    (async () => {
      // Phase 2 (chatEngineV2) / parity: one RPC returns unread for every room,
      // off the room_member_state cursor — no per-room COUNT storm, no jsonb
      // read race. Parity also gets mention_count + muted from the same call.
      if (USE_SUPABASE && (FEATURES.chatEngineV2 || FEATURES.chatParity)) {
        try {
          const rows = await loadSummaryShared(userId);
          if (dead) return;
          const next: Record<string, RoomUnreadRow> = {};
          for (const r of rows) {
            if (r.unread > 0 || r.mentionCount > 0 || r.muted) next[r.roomId] = r;
          }
          setSummary(next);
        } catch {
          if (!dead) setSummary({});
        }
        return;
      }
      const next: Record<string, RoomUnreadRow> = {};
      await Promise.all(rooms.map(async (room) => {
        if (!isRoomUnreadFor(room, userId)) return;
        try {
          if (USE_SUPABASE) {
            const n = await sbCountNewer(room.id, room.readAt?.[userId] ?? 0, 10);
            next[room.id] = row(room.id, Math.max(1, n));
            return;
          }
          const snap = await db!.collection('rooms').doc(room.id).collection('messages')
            .where('timestamp', '>', room.readAt?.[userId] ?? 0)
            .limit(10)
            .get();
          next[room.id] = row(room.id, Math.max(1, snap.size));
        } catch {
          next[room.id] = row(room.id, 1); // count is decoration; unread state is the fact
        }
      }));
      if (!dead) setSummary(next);
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, userId]);
  return summary;
}

export function useRoomUnreadCounts(
  rooms: Array<{
    id: string;
    lastMessageAt?: number;
    lastMessageSenderId?: string;
    readAt?: Record<string, number>;
  }>,
  userId: string | null | undefined
): Record<string, number> {
  const summary = useRoomUnreadSummary(rooms, userId);
  return useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of Object.values(summary)) if (r.unread > 0) counts[r.roomId] = r.unread;
    return counts;
  }, [summary]);
}

/** Number of ROOMS with unread messages — the aggregate badges (sidebar
 *  Chat, Groups tab) count conversations, not messages (per Umer,
 *  2026-08-28). Per-room chips keep the message counts above. */
export function countRoomsUnread(counts: Record<string, number>): number {
  return Object.values(counts).filter((n) => n > 0).length;
}

/** Total unread messages across all rooms (each room capped at 10).
 *  Kept from the pre-Supabase production line (copy/main-prod), where the
 *  aggregate badges summed messages; the live callers now use
 *  countRoomsUnread above. Exported so any surface still importing it
 *  keeps compiling. */
export function sumRoomUnread(counts: Record<string, number>): number {
  return Object.values(counts).reduce((s, n) => s + n, 0);
}
